import React, { useMemo, useState } from "react";
import "../../css/calculators/MortgageBorrow.css";
import PrimaryButton from "../designSystem/buttons/PrimaryButton";
import SecondaryButton from "../designSystem/buttons/SecondaryButton";
import { formatCurrency } from "./HelperFunctions/utils";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../designSystem/CustomSelect";

export default function BorrowCalculator() {
  const [formData, setFormData] = useState({
    repaymentType: "annualSalary",
    numberOfPersons: "1",
    amount: "",
    deposit: "",
    interestRate: "3.9",
    repaymentPeriod: "25",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setFormData({
      repaymentType: "annualSalary",
      numberOfPersons: "1",
      amount: "",
      deposit: "",
      interestRate: "3.9",
      repaymentPeriod: "25",
    });
  };

  // For Calculate, just re-triggers calculation (form is live, so this is visual only)
  const handleCalculate = (e) => {
    e.preventDefault();
    // No-op, as calculation is live, but could be used for analytics or UX feedback
  };

  const formatPercent = (value) => {
    return Number(value || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getMonthlyPayment = (loanAmount, annualRatePercent, years) => {
    const annualRate = Number(annualRatePercent || 0) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = Number(years || 0) * 12;

    if (loanAmount <= 0 || monthlyRate <= 0 || totalMonths <= 0) return 0;

    return (
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1)
    );
  };

  const results = useMemo(() => {
    const repaymentType = formData.repaymentType;
    const persons = Number(formData.numberOfPersons || 0);
    const amount = Number(String(formData.amount || "0").replace(/,/g, ""));
    const deposit = Number(String(formData.deposit || "0").replace(/,/g, ""));
    const interestRate = Number(formData.interestRate || 0);
    const repaymentPeriod = Number(formData.repaymentPeriod || 0);

    if (!repaymentType || !amount || !interestRate || !repaymentPeriod) {
      return null;
    }

    if (repaymentType === "annualSalary") {
      // Adjust these if you want different ranges for one/two applicants
      const minIncomeMultiple = persons === 2 ? 3.2 : 3.2;
      const maxIncomeMultiple = persons === 2 ? 4.5 : 4.5;

      const minMortgage = amount * minIncomeMultiple;
      const maxMortgage = amount * maxIncomeMultiple;

      const minMonthlyRepayment = getMonthlyPayment(
        minMortgage,
        interestRate,
        repaymentPeriod,
      );
      const maxMonthlyRepayment = getMonthlyPayment(
        maxMortgage,
        interestRate,
        repaymentPeriod,
      );

      const minPropertyPrice = minMortgage + deposit;
      const maxPropertyPrice = maxMortgage + deposit;

      const depositPctOfMinProperty =
        minPropertyPrice > 0 ? (deposit / minPropertyPrice) * 100 : 0;
      const depositPctOfMaxProperty =
        maxPropertyPrice > 0 ? (deposit / maxPropertyPrice) * 100 : 0;

      const minLtv =
        minPropertyPrice > 0 ? (minMortgage / minPropertyPrice) * 100 : 0;
      const maxLtv =
        maxPropertyPrice > 0 ? (maxMortgage / maxPropertyPrice) * 100 : 0;

      return {
        mode: "annualSalary",
        minMortgage,
        maxMortgage,
        minMonthlyRepayment,
        maxMonthlyRepayment,
        minPropertyPrice,
        maxPropertyPrice,
        depositPctHigh: depositPctOfMinProperty,
        depositPctLow: depositPctOfMaxProperty,
        ltvLow: minLtv,
        ltvHigh: maxLtv,
      };
    }

    if (repaymentType === "monthlyBudget") {
      const monthlyBudget = amount;
      const annualRate = interestRate / 100;
      const monthlyRate = annualRate / 12;
      const totalMonths = repaymentPeriod * 12;

      if (monthlyBudget <= 0 || monthlyRate <= 0 || totalMonths <= 0) {
        return null;
      }

      // Maximum borrowing from the entered monthly budget
      const mortgageAmount =
        monthlyBudget *
        ((Math.pow(1 + monthlyRate, totalMonths) - 1) /
          (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)));

      const minIncomeRequired = mortgageAmount / 4.5;
      const maxIncomeRequired = mortgageAmount / 3.2;

      const propertyPrice = mortgageAmount + deposit;
      const depositPercent =
        propertyPrice > 0 ? (deposit / propertyPrice) * 100 : 0;
      const ltv =
        propertyPrice > 0 ? (mortgageAmount / propertyPrice) * 100 : 0;

      return {
        mode: "monthlyBudget",
        mortgageAmount,
        minIncomeRequired,
        maxIncomeRequired,
        propertyPrice,
        depositPercent,
        ltv,
      };
    }

    return null;
  }, [formData]);

  return (
    <div className="borrow-calculator-page bg-white">
      <div className="borrow-form-wrap">
        <div className="title primary">About You</div>

        <div className="borrow-field-group">
          <label className="content primary">Repayment type</label>
          <div className="select-wrapper content">
            {/* <select
              name="repaymentType"
              value={formData.repaymentType}
              onChange={handleChange}
              className="body1 primary borrow-input"
            >
              <option value="">Please select</option>
              <option value="annualSalary">Annual salary</option>
              <option value="monthlyBudget">Monthly repayment budget</option>
            </select> */}
            <CustomSelect
              name="repaymentType"
              options={[
                { value: "", label: "Please select" },
                { value: "annualSalary", label: "Annual salary" },
                { value: "monthlyBudget", label: "Monthly repayment budget" },
              ]}
              value={formData.repaymentType}
              onChange={handleChange}
              placeholder="Please select repayment type"
            />

          </div>
        </div>

        <div className="borrow-field-group">
          <label className="content primary">Number of person(s)</label>
          <div className="select-wrapper">
            {/* <select
              name="numberOfPersons"
              value={formData.numberOfPersons}
              onChange={handleChange}
              className="body1 primary borrow-input"
            >
              <option value="">Please select</option>
              <option value="1">One person</option>
              <option value="2">Two people</option>
            </select> */}
            <CustomSelect
              name="numberOfPersons"
              options={[
                { value: "", label: "Please select" },
                { value: "1", label: "One person" },
                { value: "2", label: "Two people" },
              ]}
              value={formData.numberOfPersons}
              onChange={handleChange}
              placeholder="Please select number of persons"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="content primary">Amount (£)</label>
          <FormattedNumberInput
            name="amount"
            value={formData.amount}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                amount: raw,
              }))
            }
            className="body1 primary borrow-input"
          />
        </div>

        <div className="borrow-field-group">
          <label className="content primary">Deposit (£)</label>
          <FormattedNumberInput
            name="deposit"
            value={formData.deposit}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                deposit: raw,
              }))
            }
            className="body1 primary borrow-input"
          />
        </div>

        <div className="borrow-field-group">
          <label className="content primary">Interest rate (%)</label>
          <input
            type="text"
            name="interestRate"
            value={formData.interestRate}
            onChange={handleChange}
            className="body1 primary borrow-input"
          />
        </div>

        <div className="borrow-field-group">
          <label className="content primary">Repayment period (Years)</label>
          <input
            type="text"
            name="repaymentPeriod"
            value={formData.repaymentPeriod}
            onChange={handleChange}
            className="body1 primary borrow-input"
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {results && (
        <div className="borrow-result-wrap result-container">
          <div className="content">Result</div>

          {results.mode === "annualSalary" && (
            <>
              <div className="result-section">
                <div className="body1">
                  You might be offered a mortgage of:
                </div>
                <div className="content">
                  £{formatCurrency(results.minMortgage)} - £
                  {formatCurrency(results.maxMortgage)}
                </div>
                <p className="body1">
                  This range is an estimate, designed to help you understand
                  what a lender might offer you. Our estimate is based on a
                  single income multiple of
                  <strong> 3.2 to 4.5</strong>, and is subject to the
                  lender&apos;s lending criteria.
                </p>
              </div>

              <div className="result-section">
                <div className="body1">
                  Estimated monthly repayment of:
                </div>
                <div className="content">
                  £{formatCurrency(results.minMonthlyRepayment)} - £
                  {formatCurrency(results.maxMonthlyRepayment)}
                </div>
                <p className="body1">
                  Annual rate of <strong>{formData.interestRate}%</strong> over{" "}
                  <strong>{formData.repaymentPeriod} years</strong>, based on a
                  repayment mortgage.
                </p>
              </div>

              <div className="result-section">
                <div className="body1">
                  Illustrative property price range (including your deposit)
                </div>
                <div className="content">
                  £{formatCurrency(results.minPropertyPrice)} - £
                  {formatCurrency(results.maxPropertyPrice)}
                </div>
                <p className="body1">
                  Lenders typically require a deposit of at least{" "}
                  <strong>5%</strong> of the property price. Your deposit of{" "}
                  <strong>£{formatCurrency(formData.deposit)}</strong>{" "}
                  represents{" "}
                  <strong>
                    {formatPercent(results.depositPctLow)}% -{" "}
                    {formatPercent(results.depositPctHigh)}%
                  </strong>{" "}
                  of the illustrative property price, or an LTV (loan to value)
                  of{" "}
                  <strong>
                    {formatPercent(results.ltvLow)}% -{" "}
                    {formatPercent(results.ltvHigh)}%
                  </strong>
                  .
                </p>
              </div>
            </>
          )}

          {results.mode === "monthlyBudget" && (
            <>
              <div className="result-section">
                <div className="body1">
                  Based on your monthly repayment budget, you may be able to
                  borrow up to:
                </div>
                <div className="content">
                  £{formatCurrency(results.mortgageAmount)}
                </div>
                <p className="body1">
                  Annual rate of <strong>{formData.interestRate}%</strong> over{" "}
                  <strong>{formData.repaymentPeriod} years</strong>, based on a
                  repayment mortgage
                </p>
              </div>

              <div className="result-section">
                <div className="body1">
                  To qualify for a mortgage of £
                  {formatCurrency(results.mortgageAmount)}, lender will
                  typically require a gross annual income of
                </div>
                <div className="title-semibold">
                  £{formatCurrency(results.minIncomeRequired)} - £
                  {formatCurrency(results.maxIncomeRequired)}
                </div>
                <p className="content">
                  Our estimate is based on a single income multiple of{" "}
                  <strong>3.2 to 4.5</strong>, and is subject to the
                  lender&apos;s lending criteria.
                </p>
              </div>

              <div className="result-section">
                <div className="body1">
                  Illustrative property price (including your deposit)
                </div>
                <div className="content">
                  £{formatCurrency(results.propertyPrice)}
                </div>
                <p className="content">
                  Lenders typically require a deposit of at least{" "}
                  <strong>5%</strong> of the property price. Your deposit of{" "}
                  <strong>£{formatCurrency(formData.deposit)}</strong>{" "}
                  represents{" "}
                  <strong>{formatPercent(results.depositPercent)}%</strong> of
                  the illustrative property price, or an LTV (loan to value) of{" "}
                  <strong>{formatPercent(results.ltv)}%</strong>.
                </p>
              </div>
            </>
          )}

          <div className="result-divider" />

          <div className="result-note">
            <div className="body1">PLEASE NOTE:</div>
            <p className="content">
              This mortgage borrowing calculator is an interactive tool which
              should be used for your guidance only, and must not form part of
              your financial decision making process.
            </p>
            <p className="content">
              Your home may be repossessed if you do not keep up repayments on a
              mortgage or any other debt secured on it.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}